import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { Table, Tag, Avatar, Spin, Switch, Button, Tooltip, Popconfirm, message } from "antd";
import { ReloadOutlined, SearchOutlined, DownloadOutlined, ExpandOutlined, CompressOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { currentProjectAtom, canManageProjectUserAtom } from "~/stores/ProjectStore";
import { ProjectAPIs } from "~/common/typescript/apis/ProjectAPIs";
import { AC_Project__User_with_Roles } from "~/common/typescript/services/access_control/AC_Project";
import { UserOutlined, CrownOutlined } from "@ant-design/icons";
import { projectMembersLocationAtom } from "~/components/Map/Member/MemberStore";
import { userProfileModalAtom } from "~/components/Map/components/UserProfile/UserProfileStore";
import { projectMembersWithRolesAtom, projectMembersWithRolesAsyncAtom } from "~/components/Map/Member/MemberStore";
import maplibregl from 'maplibre-gl';
import MobileCardList from "../components/MobileCardList";
import { useScreenSize } from "~/hooks/useScreenSize";
import InviteMemberModal from "../components/InviteMemberModal";
import { formatDate, formatDateTime } from "~/utils/dateUtils";

interface MembersTabProps {
  tableHeight?: string;
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const MembersTab = ({ tableHeight, onMaximize, onClose, isMaximized }: MembersTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const canManageUser = useAtomValue(canManageProjectUserAtom);
  const memberLocations = useAtomValue(projectMembersLocationAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);
  const { isMobile } = useScreenSize();
  const [memberWithRoles, setMemberWithRoles] = useAtom(projectMembersWithRolesAtom);
  const fetchMembers = useSetAtom(projectMembersWithRolesAsyncAtom);
  const [loading, setLoading] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  const fetchData = async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    try {
      await fetchMembers();
    } catch (error) {
      console.error('Error fetching project members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  const handleRemoveMember = async (record: any) => {
    if (!currentProject?.id || !record?.id) return;
    try {
      await ProjectAPIs.remote_removeMemberFromProject(currentProject.id, record.id);
      message.success('Đã xoá thành viên khỏi dự án');
      fetchData();
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      message.error(error?.response?.data?.message || 'Không thể xoá thành viên');
    }
  };

  const handleToggleAutoRefresh = (checked: boolean) => {
    setAutoRefreshEnabled(checked);
    if ((window as any).toggleMemberAutoRefresh) {
      (window as any).toggleMemberAutoRefresh(checked);
    }
  };

  const handleManualRefresh = () => {
    if ((window as any).refreshMemberLocations) {
      (window as any).refreshMemberLocations();
    }
  };

  const handleMemberClick = (record: any, event: React.MouseEvent) => {
    // Check if user clicked on the avatar or name area to open profile
    const target = event.target as HTMLElement;
    const isAvatarOrNameClick = target.closest('.user-info-clickable');

    if (isAvatarOrNameClick) {
      // Open user profile modal
      setUserProfileModal({
        visible: true,
        userId: record.id
      });
      return;
    }

    // Otherwise, zoom to member location on map
    console.log('Member clicked:', record);
    console.log('Member locations from Recoil:', memberLocations);

    // Find member location from Recoil state
    const memberLocation = memberLocations.find(loc => loc.user?.id === record.id);
    console.log('Member location for ID', record.id, ':', memberLocation);

    if (memberLocation && memberLocation.location) {
      const map = window.map;
      console.log('Map object:', map);

      if (map) {
        const coords = JSON.parse(memberLocation.location);
        console.log('Flying to:', coords);

        // Zoom to member location
        map.flyTo({
          center: [coords.longitude, coords.latitude],
          zoom: 16,
          essential: true,
          duration: 1000
        });

        // Show popup at member location
        new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true
        })
          .setLngLat([coords.longitude, coords.latitude])
          .setHTML(`
            <div style="padding: 8px;">
              <strong style="font-size: 14px;">${record.fullname}</strong>
              <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
                @${record.username}
              </div>
              ${coords.accuracy ? `
                <div style="font-size: 11px; color: #94a3b8; margin-top: 8px;">
                  Độ chính xác: ${coords.accuracy.toFixed(1)}m
                </div>
              ` : ''}
              <div style="font-size: 11px; color: #94a3b8; margin-top: 8px;">
                Cập nhật: ${formatDateTime(memberLocation.updatedAt)}
              </div>
            </div>
          `)
          .addTo(map);
      } else {
        console.error('Map object not found!');
      }
    } else {
      console.warn('Không tìm thấy vị trí của thành viên:', record.fullname, 'ID:', record.id);
      console.warn('Available member locations:', memberLocations);
    }
  };

  // Prepare data for table
  const dataSource = [
    // Add project owner as first row
    ...(currentProject?.owner
      ? [
          {
            key: `owner-${currentProject.owner.id}`,
            id: currentProject.owner.id,
            fullname: currentProject.owner.fullname || currentProject.owner.username,
            username: currentProject.owner.username,
            email: currentProject.owner.email,
            phoneNumber: currentProject.owner.phoneNumber,
            roles: ['PROJECT_OWNER'],
            isOwner: true,
            gcTier: currentProject.owner.gcTier,
            gcExpireAt: currentProject.owner.gcExpireAt,
            createdAt: currentProject.owner.createdAt,
          },
        ]
      : []),
    // Add project members
    ...memberWithRoles.map((memberWithRole, index) => ({
      key: `member-${memberWithRole.user?.id}-${index}`,
      id: memberWithRole.user?.id,
      fullname: memberWithRole.user?.fullname || memberWithRole.user?.username,
      username: memberWithRole.user?.username,
      email: memberWithRole.user?.email,
      phoneNumber: memberWithRole.user?.phoneNumber,
      roles: memberWithRole.roles || [],
      isOwner: false,
      gcTier: memberWithRole.user?.gcTier,
      gcExpireAt: memberWithRole.user?.gcExpireAt,
      createdAt: memberWithRole.user?.createdAt,
    })),
  ];

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      PROJECT_COLLECTOR: 'Thu thập',
      PROJECT_ADMIN: 'Quản trị',
      PROJECT_VIEWER: 'Xem',
    };
    return roleMap[role] || role;
  };

  // Convert data for mobile card list - use same dataSource as desktop
  const mobileCardData = dataSource.map((member) => {
    const memberLocation = memberLocations.find(loc => loc.user?.id === member.id);
    const coordinates = memberLocation?.location &&
                       typeof memberLocation.location.lng === 'number' &&
                       typeof memberLocation.location.lat === 'number'
      ? `${memberLocation.location.lng.toFixed(4)}, ${memberLocation.location.lat.toFixed(4)}`
      : '';

    return {
      id: member.id?.toString() || '',
      title: member.fullname || member.username || 'Không rõ',
      username: member.username,
      description: '', // Remove generic description
      rolesArray: member.roles, // Pass roles array for Tag rendering
      isOwner: member.isOwner,
      email: member.email,
      phoneNumber: member.phoneNumber,
      tier: member.gcTier || 'BASIC',
      expireDate: member.gcExpireAt ? formatDate(member.gcExpireAt) : '',
      creator: '', // Members don't have creators
      date: formatDate(member.createdAt),
      type: member.isOwner ? 'Chủ sở hữu' : 'Thành viên',
      coordinates: coordinates,
      ...member
    };
  });

  const columns = [
    {
      title: 'Thành viên',
      dataIndex: 'fullname',
      key: 'fullname',
      width: 200,
      render: (text: string, record: any) => (
        <UserInfo className="user-info-clickable">
          <Avatar
            size={32}
            icon={record.isOwner ? <CrownOutlined /> : <UserOutlined />}
            style={{
              backgroundColor: record.isOwner ? '#ffd700' : '#3b82f6',
              color: record.isOwner ? '#000' : '#fff',
            }}
          />
          <UserDetails>
            <UserName>{text}</UserName>
            <UserSubInfo>@{record.username}</UserSubInfo>
          </UserDetails>
        </UserInfo>
      ),
    },
    {
      title: 'Liên hệ',
      dataIndex: 'contact',
      key: 'contact',
      width: 200,
      render: (_: any, record: any) => (
        <ContactInfo>
          {record.email && <ContactItem>{record.email}</ContactItem>}
          {record.phoneNumber && <ContactItem>{record.phoneNumber}</ContactItem>}
        </ContactInfo>
      ),
    },
    {
      title: 'Vai trò',
      dataIndex: 'roles',
      key: 'roles',
      width: 150,
      render: (roles: string[], record: any) => (
        <RolesContainer>
          {record.isOwner ? (
            <Tag color="gold" icon={<CrownOutlined />}>
              Chủ dự án
            </Tag>
          ) : (
            roles.map((role, index) => (
              <Tag color="blue" key={index}>
                {getRoleDisplayName(role)}
              </Tag>
            ))
          )}
        </RolesContainer>
      ),
    },
    {
      title: 'Gói',
      dataIndex: 'gcTier',
      key: 'gcTier',
      width: 120,
      render: (tier: string, record: any) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Tag color={getTierColor(tier)}>{tier || 'BASIC'}</Tag>
          {record.gcExpireAt && (
            <span style={{ fontSize: '10px', color: '#64748b' }}>
              HSD: {formatDate(record.gcExpireAt)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Tham gia',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {formatDate(date)}
        </span>
      ),
    },
    ...(canManageUser
      ? [
          {
            title: 'Thao tác',
            key: 'action',
            width: 80,
            render: (_: any, record: any) =>
              !record.isOwner ? (
                <Popconfirm
                  title="Xoá thành viên"
                  description={`Bạn có chắc chắn muốn xoá ${record.fullname || record.username} khỏi dự án?`}
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    handleRemoveMember(record);
                  }}
                  onCancel={(e) => e?.stopPropagation()}
                  okText="Xoá"
                  cancelText="Huỷ"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              ) : null,
          },
        ]
      : []),
  ];

  const getTierColor = (tier: string) => {
    const colorMap: { [key: string]: string } = {
      BASIC: 'default',
      PRO: 'gold',
      PREMIUM: 'purple',
    };
    return colorMap[tier] || 'default';
  };

  return (
    <Container>
      <ControlRow>
        <RecordCount>Số bản ghi: {dataSource.length}</RecordCount>
        <ControlActions>
          {canManageUser && (
            <Tooltip title="Mời thành viên">
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setInviteModalVisible(true)}
              />
            </Tooltip>
          )}
          <Tooltip title="Tìm kiếm">
            <Button
              type="text"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => {}}
            />
          </Tooltip>
          <Tooltip title="Tải xuống">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => {}}
            />
          </Tooltip>
          <Tooltip title="Làm mới vị trí ngay">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleManualRefresh}
            />
          </Tooltip>
          <span style={{ fontSize: 11, marginRight: 8 }}>
            Tự động (5s):
          </span>
          <Switch
            checked={autoRefreshEnabled}
            onChange={handleToggleAutoRefresh}
            size="small"
          />
          {onMaximize && (
            <Tooltip title={isMaximized ? "Thu nhỏ" : "Phóng to"}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onMaximize}
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          )}
        </ControlActions>
      </ControlRow>

      {/* Modal mời thành viên */}
      <InviteMemberModal
        visible={inviteModalVisible}
        onClose={() => setInviteModalVisible(false)}
        onSuccess={(phoneNumber) => {
          console.log('Invited member with phone:', phoneNumber);
          // Refresh member list after successful invite
          fetchData();
        }}
      />

      {isMobile ? (
        <MobileCardList
          data={mobileCardData}
          loading={loading}
          onItemClick={(item) => {
            const member = memberWithRoles.find(m => m.id?.toString() === item.id);
            if (member) {
              // Simulate click event for handleMemberClick - zoom to location
              const memberLocation = memberLocations.find(loc => loc.user?.id === member.id);
              if (memberLocation?.location && window.map) {
                window.map.flyTo({
                  center: [memberLocation.location.lng, memberLocation.location.lat],
                  zoom: 16,
                  duration: 2000
                });
              }
            }
          }}
          onCreatorClick={(creatorId) => {
            setUserProfileModal({
              visible: true,
              userId: parseInt(creatorId)
            });
          }}
          onDeleteClick={canManageUser ? (item) => {
            if (item.isOwner) return;
            handleRemoveMember(item);
          } : undefined}
          pagination={{
            current: 1,
            pageSize: 10,
            total: memberWithRoles.length,
            onChange: () => {}, // Members tab doesn't use server pagination
          }}
        />
      ) : (
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} thành viên`,
            }}
            scroll={{
              y: tableHeight ? `calc(${tableHeight} - 20px)` : 400
            }}
            size="small"
            bordered
            rowClassName={(record) => record.isOwner ? 'owner-row' : ''}
            onRow={(record) => ({
              onClick: (event) => handleMemberClick(record, event)
            })}
          />
        </Spin>
      )}
    </Container>
  );
};

export default memo(MembersTab);

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 4px;
  }

  .ant-table {
    font-size: 12px;
  }

  .ant-table-thead > tr > th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    font-weight: 600;
    font-size: 10px !important;
    color: #374151;
    border-bottom: 2px solid #e2e8f0;
    padding: 4px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px !important;
    border-bottom: 1px solid #f1f5f9;
  }

  .ant-table-measure-row td {
    padding: 0 !important;
  }

  .ant-table-tbody > tr {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(59, 130, 246, 0.05) !important;
    transform: scale(1.001);
  }

  .owner-row > td {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%) !important;
    border-left: 3px solid #ffd700;
  }

  .owner-row:hover > td {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 193, 7, 0.08) 100%) !important;
  }

  .ant-pagination {
    margin-top: 16px;
    text-align: center;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &.user-info-clickable {
    cursor: pointer;
    padding: 4px;
    margin: -4px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(59, 130, 246, 0.1);
      transform: scale(1.02);
    }
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #1e293b;
  line-height: 1.2;
`;

const UserSubInfo = styled.div`
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactItem = styled.div`
  font-size: 11px;
  color: #374151;
  line-height: 1.3;
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    padding: 4px 8px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const RecordCount = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RolesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .ant-tag {
    margin: 0;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    line-height: 1.2;
  }
`;